import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Button,
  IconButton,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Snackbar,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  useTheme,
  FormControlLabel,
  Switch,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Tooltip,
  TableContainer,
} from "@mui/material";
import { Edit, Delete, CheckCircle, Cancel } from "@mui/icons-material";

import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import PauseCircleIcon from "@mui/icons-material/PauseCircle";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import AutorenewIcon from "@mui/icons-material/Autorenew";
import InfoIcon from "@mui/icons-material/Info";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import CancelIcon from "@mui/icons-material/Cancel";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";
import DoneAllIcon from "@mui/icons-material/DoneAll";
import LoopIcon from "@mui/icons-material/Loop";
import AddIcon from "@mui/icons-material/Add";

import { grey, blueGrey, lightGreen } from "@mui/material/colors";
import api from "../../utils/api";

const customColors = {
  grey: grey[500],
  blueGrey: blueGrey[500],
  lightGreen: lightGreen[500],
};

const colorOptions = [
  { label: "Primary", value: "primary" },
  { label: "Secondary", value: "secondary" },
  { label: "Success", value: "success" },
  { label: "Error", value: "error" },
  { label: "Warning", value: "warning" },
  { label: "Info", value: "info" },
  { label: "Inherit", value: "inherit" },
  { label: "Dark (Custom)", value: "grey" },
  { label: "BlueGrey", value: "blueGrey" },
  { label: "Light Green", value: "lightGreen" },
];

const iconOptions = [
  { label: "PlayArrow", value: "PlayArrow", icon: <PlayArrowIcon fontSize="small" /> },
  { label: "PauseCircle", value: "PauseCircle", icon: <PauseCircleIcon fontSize="small" /> },
  { label: "CheckCircle", value: "CheckCircle", icon: <CheckCircleIcon fontSize="small" /> },
  { label: "Autorenew", value: "Autorenew", icon: <AutorenewIcon fontSize="small" /> },
  { label: "Info", value: "Info", icon: <InfoIcon fontSize="small" /> },
  { label: "WarningAmber", value: "WarningAmber", icon: <WarningAmberIcon fontSize="small" /> },
  { label: "Cancel", value: "Cancel", icon: <CancelIcon fontSize="small" /> },
  { label: "HourglassEmpty", value: "HourglassEmpty", icon: <HourglassEmptyIcon fontSize="small" /> },
  { label: "DoneAll", value: "DoneAll", icon: <DoneAllIcon fontSize="small" /> },
  { label: "Loop", value: "Loop", icon: <LoopIcon fontSize="small" /> },
];

const getIconComponent = (value) => {
  const match = iconOptions.find((i) => i.value === value);
  return match ? match.icon : null;
};

const StatusesPage = () => {
  const theme = useTheme();
  const [statuses, setStatuses] = useState([]);
  const [modules, setModules] = useState([]);
  const [form, setForm] = useState({
    module_id: "",
    statusname: "",
    alert_time: "",
    icon: "",
    color: "",
    is_billable_trigger: false,
  });
  const [editId, setEditId] = useState(null);
  const [open, setOpen] = useState(false);
  const [snack, setSnack] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  useEffect(() => {
    fetchStatuses();
    fetchModules();
  }, []);

  const fetchStatuses = async () => {
    try {
      const res = await api.get("/statuses");
      setStatuses(res.data);
    } catch (error) {
      console.error("Error fetching statuses:", error);
    }
  };

  const fetchModules = async () => {
    try {
      const res = await api.get("/modules");
      setModules(res.data);
    } catch (error) {
      console.error("Error fetching modules:", error);
    }
  };

  const getModuleName = (id) => {
    const mod = modules.find((m) => String(m.id) === String(id));
    return mod ? mod.name : id;
  };

  const getColorValue = (colorName) => {
    return theme.palette[colorName]?.main || customColors[colorName] || colorName;
  };

  const handleOpenForm = (status = null) => {
    if (status) {
      setForm({
        ...status,
        module_id: String(status.module_id),
        is_billable_trigger: !!status.is_billable_trigger,
      });
      setEditId(status.id);
    } else {
      setForm({
        module_id: "",
        statusname: "",
        alert_time: "",
        icon: "",
        color: "",
        is_billable_trigger: false,
      });
      setEditId(null);
    }
    setOpen(true);
  };

  const handleCloseForm = () => {
    setOpen(false);
    setEditId(null);
    setForm({
      module_id: "",
      statusname: "",
      alert_time: "",
      icon: "",
      color: "",
      is_billable_trigger: false,
    });
  };

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async () => {
    try {
      const payload = { ...form };

      if (editId) {
        await api.put(`/statuses/${editId}`, payload);
        await api.post(`/billable-status-settings`, {
          module_id: form.module_id,
          status_id: editId,
          is_billable_trigger: form.is_billable_trigger ? 1 : 0,
        });
        setSnack({ open: true, message: "Status updated", severity: "success" });
      } else {
        const res = await api.post("/statuses", payload);
        const newStatusId = res.data.id;
        await api.post(`/billable-status-settings`, {
          module_id: form.module_id,
          status_id: newStatusId,
          is_billable_trigger: form.is_billable_trigger ? 1 : 0,
        });
        setSnack({ open: true, message: "Status added", severity: "success" });
      }

      handleCloseForm();
      fetchStatuses();
    } catch (error) {
      console.error(error);
      setSnack({ open: true, message: "Save failed", severity: "error" });
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this status?")) return;
    try {
      await api.delete(`/statuses/${id}`);
      fetchStatuses();
      setSnack({ open: true, message: "Deleted successfully", severity: "success" });
    } catch (err) {
      setSnack({ open: true, message: "Delete failed", severity: "error" });
    }
  };

  const fieldSx = {
    mt: 1.25,
    "& .MuiOutlinedInput-root": {
      backgroundColor: "rgba(255,255,255,0.96)",
      borderRadius: "10px",
    },
  };

  const dialogPaperSx = {
    borderRadius: "18px",
    background: "rgba(255,255,255,0.96)",
    backdropFilter: "blur(10px)",
    boxShadow: "0 20px 60px rgba(0,0,0,0.25)",
    overflow: "hidden",
  };

  const primaryButtonSx = {
    backgroundColor: "#2f63ad",
    color: "white",
    fontWeight: 700,
    borderRadius: "10px",
    textTransform: "none",
    px: 2.25,
    boxShadow: "none",
    "&:hover": {
      backgroundColor: "#1f4f94",
      boxShadow: "none",
    },
  };

  const secondaryButtonSx = {
    borderRadius: "10px",
    textTransform: "none",
    fontWeight: 600,
  };

  return (
    <Paper
      elevation={0}
      sx={{
        maxWidth: 1100,
        mx: "auto",
        mt: 1,
        p: { xs: 2, md: 3 },
        borderRadius: "18px",
        border: "1px solid rgba(47,99,173,0.08)",
        boxShadow: "0 8px 24px rgba(0,0,0,0.08)",
        backgroundColor: "rgba(255,255,255,0.96)",
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: { xs: "flex-start", sm: "center" },
          flexDirection: { xs: "column", sm: "row" },
          gap: 2,
          mb: 3,
        }}
      >
        <Box>
          <Typography
            variant="h5"
            sx={{
              fontWeight: 700,
              color: "#223142",
              mb: 0.5,
            }}
          >
            Statuses
          </Typography>

          <Typography
            variant="body2"
            sx={{
              color: "#6b778c",
            }}
          >
            Manage module statuses, alert times, icons, colors, and billable triggers.
          </Typography>
        </Box>

        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenForm()}
          sx={primaryButtonSx}
        >
          Add Status
        </Button>
      </Box>

      <Paper
        elevation={0}
        sx={{
          borderRadius: "16px",
          border: "1px solid rgba(47,99,173,0.08)",
          boxShadow: "0 8px 24px rgba(0,0,0,0.06)",
          overflow: "hidden",
        }}
      >
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 700 }}>Status Name</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Module</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Alert Time</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Icon</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Color</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Billable</TableCell>
                <TableCell sx={{ fontWeight: 700 }} align="right">
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {statuses.map((status) => (
                <TableRow key={status.id}>
                  <TableCell>{status.statusname}</TableCell>
                  <TableCell>{getModuleName(status.module_id)}</TableCell>
                  <TableCell>{status.alert_time || "—"}</TableCell>
                  <TableCell>{getIconComponent(status.icon)}</TableCell>
                  <TableCell>
                    <Tooltip title={status.color || "No color"}>
                      <Box
                        sx={{
                          width: 20,
                          height: 20,
                          borderRadius: "50%",
                          backgroundColor: getColorValue(status.color),
                          border: "1px solid #d9e2ec",
                        }}
                      />
                    </Tooltip>
                  </TableCell>
                  <TableCell>
                    {status.is_billable_trigger ? (
                      <CheckCircle color="success" fontSize="small" />
                    ) : (
                      <Cancel color="disabled" fontSize="small" />
                    )}
                  </TableCell>
                  <TableCell align="right">
                    <IconButton onClick={() => handleOpenForm(status)}>
                      <Edit />
                    </IconButton>
                    <IconButton onClick={() => handleDelete(status.id)} color="error">
                      <Delete />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}

              {!statuses.length && (
                <TableRow>
                  <TableCell colSpan={7} sx={{ color: "text.secondary" }}>
                    No statuses found.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <Dialog
        open={open}
        onClose={handleCloseForm}
        fullWidth
        maxWidth="sm"
        PaperProps={{ sx: dialogPaperSx }}
      >
        <DialogTitle
          sx={{
            textAlign: "center",
            fontWeight: 700,
            fontSize: "1.35rem",
            pt: 3,
            pb: 1,
            color: "#1f1f1f",
          }}
        >
          {editId ? "Edit Status" : "Add Status"}
        </DialogTitle>

        <DialogContent sx={{ px: 3, pb: 1 }}>
          <Typography
            sx={{
              mt: 0.5,
              mb: 1,
              textAlign: "center",
              color: "#666",
              fontSize: "0.95rem",
            }}
          >
            Configure the status details below.
          </Typography>

          <FormControl fullWidth sx={fieldSx}>
            <InputLabel>Module</InputLabel>
            <Select
              name="module_id"
              value={form.module_id}
              onChange={handleChange}
              label="Module"
            >
              {modules.map((mod) => (
                <MenuItem key={mod.id} value={String(mod.id)}>
                  {mod.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            fullWidth
            label="Status Name"
            name="statusname"
            value={form.statusname}
            onChange={handleChange}
            sx={fieldSx}
          />

          <TextField
            fullWidth
            label="Alert Time"
            name="alert_time"
            value={form.alert_time}
            onChange={handleChange}
            sx={fieldSx}
          />

          <FormControl fullWidth sx={fieldSx}>
            <InputLabel>Icon</InputLabel>
            <Select
              name="icon"
              value={form.icon}
              onChange={handleChange}
              label="Icon"
            >
              {iconOptions.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  <Box display="flex" alignItems="center" gap={1}>
                    {opt.icon}
                    {opt.label}
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl fullWidth sx={fieldSx}>
            <InputLabel>Color</InputLabel>
            <Select
              name="color"
              value={form.color}
              onChange={handleChange}
              label="Color"
            >
              {colorOptions.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  <Box display="flex" alignItems="center" gap={1.25} width="100%">
                    <Box
                      sx={{
                        width: 18,
                        height: 18,
                        borderRadius: "50%",
                        backgroundColor: getColorValue(opt.value),
                        border: "1px solid #ccc",
                        flexShrink: 0,
                      }}
                    />
                    <Typography variant="body2">{opt.label}</Typography>
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Box
            sx={{
              mt: 2,
              px: 1,
              py: 1.25,
              borderRadius: "12px",
              backgroundColor: "rgba(47,99,173,0.04)",
              border: "1px solid rgba(47,99,173,0.08)",
            }}
          >
            <FormControlLabel
              control={
                <Switch
                  checked={form.is_billable_trigger}
                  onChange={(e) =>
                    setForm({ ...form, is_billable_trigger: e.target.checked })
                  }
                />
              }
              label="Billable Status"
              sx={{ m: 0 }}
            />
          </Box>
        </DialogContent>

        <DialogActions
          sx={{
            px: 3,
            pb: 3,
            pt: 1.5,
            justifyContent: "center",
            gap: 1.25,
          }}
        >
          <Button onClick={handleCloseForm} variant="outlined" sx={secondaryButtonSx}>
            Cancel
          </Button>

          <Button onClick={handleSubmit} variant="contained" sx={primaryButtonSx}>
            Save
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snack.open}
        autoHideDuration={3000}
        onClose={() => setSnack({ ...snack, open: false })}
      >
        <Alert
          severity={snack.severity}
          onClose={() => setSnack({ ...snack, open: false })}
        >
          {snack.message}
        </Alert>
      </Snackbar>
    </Paper>
  );
};

export default StatusesPage;